import type {
    EcapitalDebtorSearchResult,
    EcapitalPrepareSubmissionResponse,
    EcapitalSearchDebtorsResponse,
    EcapitalSubmissionPackage,
} from '@numeo/types-dispatcher-ui'
import axios from 'axios'
import { EXTENSION_ID } from './constants'
import { isExtensionAvailable } from './extension'

interface EcapitalSubmissionPackageResponse {
    success: boolean
    data?: EcapitalSubmissionPackage
    message?: string
}

export async function fetchEcapitalSubmissionPackage(invoiceId: string): Promise<EcapitalSubmissionPackage> {
    const response = await axios.get<EcapitalSubmissionPackageResponse>(`/factoring/ecapital/submission-package/${invoiceId}`)
    if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || 'Failed to build eCapital submission package')
    }
    return response.data.data
}

// Persists the eCapital LoadKey(s) and uploaded document type(s) the extension just created/attached
// onto their own load row (`invoice.loads[].ecapitalLoadKey`/`ecapitalUploadedDocumentTypes`,
// addressed by ongoingLoadId — never one shared invoice-level key, which would misattribute every
// load after the first in a multi-load invoice), so the NEXT submission package request carries
// each one back as `existingLoadKey`/`uploadedDocumentTypes`. Called after every submission attempt
// that got at least as far as creating a load or uploading a document, success or not.
//
// Two flags, both false by default, both needing the caller's claim: `fundingConfirmed` stamps the invoice as sent to eCapital
// (`ecapitalFundingConfirmedAt`, a name from when the funding was requested too; it now records the send), and `completed`
// moves the invoice to FACTORED. They go as two calls so a failure of the second cannot erase the first.
// Like every other admin service call here, this never throws — the caller checks `.success`.
export async function saveEcapitalLoadKey(
    invoiceId: string,
    loadKeys: Record<string, string>,
    uploadedDocumentTypes?: Record<string, string[]>,
    options?: { completed?: boolean; fundingConfirmed?: boolean; claimId?: string }
): Promise<{ success: boolean; message?: string }> {
    const ongoingLoadIds = new Set([...Object.keys(loadKeys), ...Object.keys(uploadedDocumentTypes || {})])
    const entries = Array.from(ongoingLoadIds)
        .map((ongoingLoadId) => ({
            ongoingLoadId,
            loadKey: loadKeys[ongoingLoadId],
            uploadedDocumentTypes: uploadedDocumentTypes?.[ongoingLoadId],
        }))
        .filter((entry) => entry.loadKey || entry.uploadedDocumentTypes)
    if (entries.length === 0) {
        // A `completed`/`fundingConfirmed` call with nothing to persist is a caller bug: report it rather than claim success.
        return options?.completed || options?.fundingConfirmed ? { success: false, message: 'No eCapital load progress to persist' } : { success: true }
    }
    try {
        const response = await axios.post(`/factoring/ecapital/load-key/${invoiceId}`, {
            loadKeys: entries,
            completed: options?.completed,
            fundingConfirmed: options?.fundingConfirmed,
            // Recording the send or completing is the claim holder's act; the server refuses it without the claim.
            claimId: options?.claimId,
        })
        return response.data
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return { success: false, message: error.response?.data?.message || 'Failed to save eCapital submission progress' }
        }
        return { success: false, message: 'Failed to save eCapital submission progress' }
    }
}

// Reserves the invoice for this browser's submission. `POST load` has no idempotency key, so a
// second dispatcher submitting the same invoice at the same moment would create its loads twice; the server
// answers 409 to whoever does not hold the claim. Fails closed: any answer other than success means "do not submit".
export async function claimEcapitalSubmission(invoiceId: string): Promise<{ success: boolean; message?: string; claimId?: string; status?: number }> {
    try {
        const response = await axios.post(`/factoring/ecapital/submission-claim/${invoiceId}`)
        const claimId = response.data?.data?.claimId
        // No claimId means nothing could ever release it, so it is no claim at all.
        return response.data?.success && typeof claimId === 'string' ? { success: true, claimId } : { success: false, message: response.data?.message }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            return {
                success: false,
                status: error.response?.status,
                message: error.response?.data?.message || 'Could not reserve the invoice for eCapital submission — try again',
            }
        }
        return { success: false, message: 'Could not reserve the invoice for eCapital submission — try again' }
    }
}

// Keeps the reservation alive while a long submission runs.
//  - 'renewed':     the server extended the lease.
//  - 'lost':        the claim is certainly not this browser's any more (409 another browser took the invoice over or it moved on,
//                   403 this user may no longer edit accounting, 404 the invoice is gone), so the caller can say so.
//  - 'unreachable': the beat could not be delivered or the server failed (network, 401, 5xx). That proves nothing either way.
export type EcapitalRenewResult = 'renewed' | 'lost' | 'unreachable'

const CLAIM_LOST_STATUSES: readonly number[] = [403, 404, 409]

export async function renewEcapitalSubmission(invoiceId: string, claimId: string): Promise<EcapitalRenewResult> {
    try {
        await axios.put(`/factoring/ecapital/submission-claim/${invoiceId}`, undefined, { params: { claimId } })
        return 'renewed'
    } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined
        return status !== undefined && CLAIM_LOST_STATUSES.includes(status) ? 'lost' : 'unreachable'
    }
}

// Best effort: a claim nobody releases runs out on its own. The claimId is what proves it is this browser's claim.
export async function releaseEcapitalSubmission(invoiceId: string, claimId: string): Promise<void> {
    try {
        await axios.delete(`/factoring/ecapital/submission-claim/${invoiceId}`, { params: { claimId } })
    } catch {
        // The claim expires by itself.
    }
}

// A small, bounded set of failure buckets for Amplitude — the raw error message includes broker/
// debtor names and HTTP response bodies (free text, unbounded property cardinality); this collapses
// it to a fixed enum before it's ever sent as an event property. Matched against the exact message
// shapes `ecapitalContent.ts` and this file construct, so a new failure mode should extend this list
// rather than let its raw text leak through as 'unknown'.
type EcapitalFailureReason =
    | 'client_disconnected'
    | 'portal_tab_closed'
    | 'session_expired'
    | 'missing_documents'
    | 'invalid_package'
    | 'invoice_date_rejected'
    | 'account_blocked'
    | 'debtor_not_found'
    | 'debtor_search_failed'
    | 'already_submitted'
    | 'load_create_failed'
    | 'document_download_failed'
    | 'document_upload_failed'
    | 'not_ready_for_funding'
    | 'duplicate_invoice_number'
    | 'extension_unavailable'
    | 'account_mismatch'
    | 'integration_disabled'
    | 'unknown'

export function categorizeEcapitalFailureReason(message: string | undefined): EcapitalFailureReason {
    if (!message) return 'unknown'
    if (message.includes('client number not found')) return 'client_disconnected'
    // The extension found no eCapital portal tab to run the submission in; every invoice of a batch would meet the same wall.
    if (message.includes('portal tab is not open')) return 'portal_tab_closed'
    // A refused document download is the document's problem, not the login's, whatever its status says.
    if (message.startsWith('Failed to download')) return 'document_download_failed'
    // The portal's API answers 401 {"error":"Invalid token"} once the login has expired. Matched on the wording of an API call
    // ("eCapital <endpoint> failed (401)"), so only the portal's own refusal marks the login expired for the whole project.
    if (/\beCapital [\w./-]+ failed \(401\)/.test(message)) return 'session_expired'
    if (message.includes('Missing required document')) return 'missing_documents'
    if (message.includes('already submitted at eCapital')) return 'already_submitted'
    if (message.includes('not allowed to add loads') || message.includes('ineligible for funding')) return 'account_blocked'
    if (message.includes('invoice date')) return 'invoice_date_rejected'
    if (message.includes('already used on another invoice')) return 'duplicate_invoice_number'
    if (message.includes('did not mark load') || message.includes('does not generate invoices automatically')) return 'not_ready_for_funding'
    if (message.includes('No eCapital debtor found') || message.includes('no DebtorKey')) return 'debtor_not_found'
    if (message.includes('debtor/search failed')) return 'debtor_search_failed'
    if (message.includes('did not return a loadKey') || message.includes('eCapital load failed')) return 'load_create_failed'
    if (message.includes('document/upload failed') || message.includes('load/document failed')) return 'document_upload_failed'
    if (message.includes('Cannot submit to eCapital')) return 'invalid_package'
    if (message.includes('Numeo Chrome Extension')) return 'extension_unavailable'
    // The same wall for every invoice: the tab is logged in to another eCapital account than the one connected, or the
    // connection cannot be tied to one. Matched on the extension's own wording (ecapitalPreflight.ts).
    if (
        message.includes('is logged in as client') ||
        message.includes('did not say which account') ||
        message.includes('Cannot confirm which eCapital account') ||
        message.includes('answered for client') ||
        message.includes('Reconnect eCapital in Numeo')
    ) {
        return 'account_mismatch'
    }
    // The server refuses every claim while eCapital is switched off for the project or is not its active provider.
    if (message.includes('turned off for this project') || message.includes('is not the active factoring provider')) return 'integration_disabled'
    return 'unknown'
}

// The message provably never reached the extension (not installed, asleep without a listener, the tab is gone): nothing was
// sent. Any other failure to get an answer back may come after some loads were created; a new submission reuses those.
const isMessageNeverDelivered = (lastError: string): boolean => /receiving end does not exist|could not establish connection|no tab with id/i.test(lastError)

const KNOWN_REPLY_STATUSES: readonly string[] = ['success', 'error', 'blocked']

const lostReply = (reason: string): EcapitalPrepareSubmissionResponse => ({
    status: 'error',
    message: `${reason} Some loads may already be in eCapital — submit this invoice again, they are reused.`,
})

export async function submitEcapitalSubmission(packageData: EcapitalSubmissionPackage): Promise<EcapitalPrepareSubmissionResponse> {
    if (!isExtensionAvailable()) {
        return { status: 'error', message: 'Numeo Chrome Extension is required to submit to eCapital.' }
    }

    return new Promise((resolve) => {
        chrome.runtime.sendMessage(
            EXTENSION_ID,
            {
                type: 'ECAPITAL_PREPARE_SUBMISSION',
                submissionPackage: packageData,
            },
            (response?: EcapitalPrepareSubmissionResponse) => {
                const lastError = chrome.runtime.lastError?.message
                if (lastError) {
                    if (isMessageNeverDelivered(lastError)) {
                        resolve({ status: 'error', message: lastError })
                        return
                    }
                    // The message arrived and the extension then stopped answering mid-run (the worker or the extension restarted,
                    // or any wording of that).
                    resolve(lostReply(`The Numeo extension stopped answering while the submission was running (${lastError}).`))
                    return
                }
                // The message was delivered but no usable answer came back.
                if (!response) {
                    resolve(lostReply('The Numeo extension sent no answer to the submission.'))
                    return
                }
                if (!KNOWN_REPLY_STATUSES.includes(response.status)) {
                    resolve(lostReply('The Numeo extension answered the submission in a way Numeo does not understand.'))
                    return
                }
                resolve(response)
            }
        )
    })
}

/** The customer search could not run (the portal tab is closed, the login expired, the extension did not answer): not the same as "no customer matches". */
export class EcapitalSearchUnavailableError extends Error {
    constructor(
        message: string,
        readonly needsReauth = false
    ) {
        super(message)
        this.name = 'EcapitalSearchUnavailableError'
    }
}

interface EcapitalDebtorSearchOutcome {
    success: boolean
    data?: EcapitalDebtorSearchResult[]
    exactDebtorKey?: string
    message?: string
    /** The portal tab is closed or its session expired — the picker offers "Reconnect eCapital". */
    needsReauth?: boolean
}

/**
 * Searches eCapital's own customer directory (`debtor/search`) through the extension, which runs it from
 * the dispatcher's portal tab — the only place the eCapital API is reachable. Read-only.
 */
export async function searchEcapitalDebtors(search: string, mcNumber?: string): Promise<EcapitalDebtorSearchOutcome> {
    if (!isExtensionAvailable()) {
        return { success: false, message: 'Numeo Chrome Extension is required to search eCapital customers.' }
    }

    return new Promise((resolve) => {
        chrome.runtime.sendMessage(EXTENSION_ID, { type: 'ECAPITAL_SEARCH_DEBTORS', search, mcNumber }, (response?: EcapitalSearchDebtorsResponse) => {
            if (chrome.runtime.lastError) {
                resolve({ success: false, needsReauth: true, message: chrome.runtime.lastError.message || 'Failed to reach Numeo Chrome Extension.' })
                return
            }
            if (!response || response.status !== 'success') {
                resolve({ success: false, needsReauth: response?.needsReauth, message: response?.message || 'eCapital customer search failed.' })
                return
            }
            resolve({ success: true, data: response.results ?? [], exactDebtorKey: response.exactDebtorKey })
        })
    })
}
